"use client";

import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Mission() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const origins = [{
    coords: "01.001",
    name: "NETWORK"
  }, {
    coords: "01.002",
    name: "SECURITY"
  }, {
    coords: "01.003",
    name: "ACCESS"
  }];
  const stats = [{
    label: "EST. FOUNDED",
    value: "2024"
  }, {
    label: "SYSTEMS DEPLOYED",
    value: "128"
  }, {
    label: "UPTIME RATIO",
    value: "99.9%"
  }];
  return <section data-section-id="1302" id="our-mission" className="bg-background text-foreground relative overflow-hidden border-b border-border">
      {}
      <div className="absolute bottom-0 left-0 right-0 h-4 pointer-events-none" style={{
      backgroundImage: "repeating-linear-gradient(to right, var(--secondary) 0 4px, transparent 4px 10px)",
      backgroundSize: "10px 2px",
      backgroundPosition: "0 center",
      backgroundRepeat: "repeat-x"
    }} aria-hidden="true" />

      <div className="relative max-w-[1360px] mx-auto px-6 md:px-10 py-28 md:py-40">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute select-none font-serif text-[240px] md:text-[360px] leading-none opacity-[0.03] top-12 md:top-16 left-2 md:left-6" style={{
        WebkitTextStroke: "1.5px var(--primary)",
        color: "transparent"
      }}>
          01
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={titleRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            {}
            <div className="font-mono uppercase text-xs md:text-sm tracking-[0.18em] text-primary mb-4">
              § 01 — CORPORATE ARCHITECTURE
            </div>

            {}
            <div className="flex items-center gap-2 mb-10">
              <Separator className="bg-primary/60 flex-1 h-px" />
              <span className="w-2 h-2 bg-primary" aria-hidden="true" />
            </div>

            {}
            <h2 className="font-default font-bold text-foreground leading-[1.02] tracking-tight" style={{
            fontSize: "clamp(2rem, 4.4vw, 4rem)",
            letterSpacing: "-0.015em"
          }}>
              Unified{" "}
              <span className="relative inline-block italic">
                <span className="relative z-10 text-primary">Solutions</span>
                <span className={`absolute left-0 bottom-0.5 h-[2px] bg-primary transition-[width] duration-700 ${titleVisible ? "w-full" : "w-0"}`} style={{
                transitionDelay: "200ms",
                transitionTimingFunction: "cubic-bezier(0.65, 0, 0.35, 1)"
              }} aria-hidden="true" />
              </span>{" "}
              for modern business.
            </h2>

            {}
            <ul className="mt-12 space-y-4">
              {origins.map((origin, i) => <li data-index={i} key={origin.name} className="flex items-center gap-3 font-mono text-xs md:text-sm tracking-[0.1em] text-foreground/85">
                  <span className="w-2 h-2 bg-primary shrink-0" aria-hidden="true" />
                  <span className="text-primary">{origin.coords}</span>
                  <span className="text-foreground/60">·</span>
                  <span className="text-foreground">{origin.name}</span>
                </li>)}
            </ul>
          </div>

          {}
          <div ref={textRef} className={`lg:col-span-7 relative transition-all duration-700 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionDelay: "120ms"
        }}>
            {}
            <div className="font-default text-foreground/90 leading-[1.65]" style={{
            columnCount: 1,
            columnGap: "48px",
            fontSize: "clamp(1rem, 1.1vw, 1.0625rem)"
          }}>
              <div className="md:[column-count:2] md:[column-gap:48px]">
                <p className="mb-5">
                  <span className="float-left font-default text-primary mr-2 leading-[0.85] pt-1 relative" style={{
                  fontSize: "5em"
                }}>
                    B
                    <span className="absolute left-0 right-2 bottom-1 h-[2px] bg-primary" aria-hidden="true" />
                  </span>
                  etaquest, LLC specializes in unified IT solutions that are tailored to your business needs. Our systems integrate seamlessly to improve performance, security, and scalability. We act as the architects of your digital infrastructure, ensuring every component communicates with precision.
                </p>
                <p className="mb-5">
                  Our team focuses on high-speed networking, advanced video surveillance, and robust access control. By leveraging Ubiquiti UniFi technologies, we provide a cohesive ecosystem that is both manageable and infinitely scalable for growing enterprises.
                </p>
                <p>
                  We believe in building systems that disappear into the background, leaving only performance and reliability. From automated ISP failover to AI-powered security alerts, we ensure your business stays online and protected.
                </p>
              </div>
            </div>

            {}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {stats.map((stat, i) => <div data-index={i} key={stat.label} className="bg-card text-card-foreground border border-border p-6 transition-colors duration-300">
                  <div className="font-mono uppercase text-[0.7rem] tracking-[0.18em] text-card-foreground/70 mb-3">
                    {stat.label}
                  </div>
                  <div className="font-default font-bold leading-none text-primary" style={{
                fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)",
                letterSpacing: "-0.015em"
              }}>
                    {stat.value}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Separator className="bg-card-foreground/40 flex-1 h-px" />
                    <span className="w-1.5 h-1.5 bg-primary" aria-hidden="true" />
                  </div>
                </div>)}
            </div>

            {}
            <div className="mt-14 flex justify-start">
              <div className="group relative w-36 h-36 md:w-44 md:h-44 transition-transform duration-700 ease-in-out hover:-rotate-6" aria-hidden="true">
                <div className="absolute inset-0 border border-primary/80" />
                <div className="absolute inset-[6px] border border-primary/70" />
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                  <defs>
                    <path id="stamp-top-arc-cp" d="M 14,50 A 36,36 0 0 1 86,50" fill="none" />
                    <path id="stamp-bottom-arc-cp" d="M 14,50 A 36,36 0 0 0 86,50" fill="none" />
                  </defs>
                  <text className="fill-primary" style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "6px",
                  letterSpacing: "2px",
                  textTransform: "uppercase"
                }}>
                    <textPath href="#stamp-top-arc-cp" startOffset="50%" textAnchor="middle">
                      BETAQUEST TECH
                    </textPath>
                  </text>
                  <text className="fill-primary" style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "5px",
                  letterSpacing: "2px",
                  textTransform: "uppercase"
                }}>
                    <textPath href="#stamp-bottom-arc-cp" startOffset="50%" textAnchor="middle">
                      UNIFIED · SYSTEMS
                    </textPath>
                  </text>
                </svg>
                {}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 text-primary" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.3 6.3l1.4 1.4M16.3 16.3l1.4 1.4M6.3 17.7l1.4-1.4M16.3 7.7l1.4-1.4" />
                  </svg>
                  <span className="mt-1.5 italic font-default text-foreground/80" style={{
                  fontSize: "0.625rem"
                }}>
                    est. 2024
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}